// src/pages/SavedRoutes.js
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import API from "../utils/api";
import { formatStateAbbr } from "../constants/usStates";
import { formatPhoneDisplay } from "../utils/formatPhone";

import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  IconButton,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  keyframes,
  Stack,
  Chip,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import NavigationIcon from "@mui/icons-material/Navigation";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircle from "@mui/icons-material/CheckCircle";
import DirectionsIcon from "@mui/icons-material/Directions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";

import STATUS_COLORS from "../utils/statusColors";
import { openNavigation } from "../utils/navigation";
import { emitLeadUpdate, subscribeLeadUpdates } from "../utils/leadSync";
import StatusUpdateDialog from "../components/StatusUpdateDialog";
import LeadActionPanel from "../components/LeadActionPanel";
import { isMobile as isDeviceMobile } from "../utils/deviceDetection";
import useLandscape from "../hooks/useLandscape";
import useAuth from "../hooks/useAuth"; 
import useTour from "../tour/useTour";
import { SAVED_ROUTES_TOUR_STEPS } from "../tour/steps/saved-routes.steps";
import {
  shouldShowSavedRoutesMap,
} from "../constants/savedRoutesFeatures";

// -------------------------------
// Pulsing Animation for Current Stop
// -------------------------------
const pulseHalo = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(46, 204, 113, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.4);
  }
`;

// -------------------------------
// Reverse Geocode Helper
// -------------------------------
const reverseGeocode = async (lat, lon) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`;
    const res = await axios.get(url);
    const addr = res.data?.address || {};

    const streetName =
      addr.road ||
      addr.residential ||
      addr.pedestrian ||
      addr.footway ||
      addr.neighbourhood ||
      "";

    const streetLine = [addr.house_number, streetName]
      .filter(Boolean)
      .join(" ");

    const city = addr.city || addr.town || addr.village || addr.hamlet;
    const state = formatStateAbbr(addr.state_code || addr.state);
    const zip = addr.postcode;

    const formatted = [streetLine, city, state, zip]
      .filter(Boolean)
      .join(", ");

    return (
      formatted ||
      res.data.display_name ||
      `Lat: ${lat.toFixed(4)}, Lng: ${lon.toFixed(4)}`
    );
  } catch {
    return `Lat: ${lat.toFixed(4)}, Lng: ${lon.toFixed(4)}`;
  }
};

// Mobile stops breakdown — current-stop highlight (pattern #1: pill + full ring + tint)
const MOBILE_CURRENT_STOP = {
  green: "#22c55e",
  tint: "rgba(34, 197, 94, 0.10)",
};

const mobileRouteStopCardSx = (isCurrent, isVisited) => ({
  borderRadius: 2,
  cursor: "pointer",
  opacity: isCurrent ? 1 : isVisited ? 0.65 : 1,
  border: isCurrent ? `2px solid ${MOBILE_CURRENT_STOP.green}` : "1px solid",
  borderColor: isCurrent ? MOBILE_CURRENT_STOP.green : "divider",
  bgcolor: isCurrent ? MOBILE_CURRENT_STOP.tint : "background.paper",
  transition: "border-color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease",
});

const mobileCurrentStopPillSx = {
  height: 22,
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  bgcolor: MOBILE_CURRENT_STOP.green,
  color: "#fff",
  "& .MuiChip-label": { px: 1 },
};

// Future: de-emphasized inactive rows (#5) can read cleaner on long routes; chevrons alone
// may look like "next stop" — keep an explicit Update action if we adopt that pattern.

// -------------------------------
// Marker Helpers
// -------------------------------
const createModernPin = (color) =>
  L.divIcon({
    className: "modern-pin",
    html: `<div style="
      width:22px;
      height:22px;
      border-radius:50%;
      background:${color};
      border:2px solid #fff;
      box-shadow:0 0 4px rgba(0,0,0,0.4);
    "></div>`,
    iconAnchor: [11, 11],
  });

const getPinColor = (status = "New") => {
  const match = Object.entries(STATUS_COLORS).find(
    ([label]) => label.toLowerCase() === status.toLowerCase()
  );
  return match ? match[1] : "#42A5F5";
};

const createNumberedPin = (num, color, isCurrent, isVisited) =>
  L.divIcon({
    className: "numbered-pin",
    html: `
      <div style="
        width:30px;
        height:30px;
        border-radius:50%;
        background:${color};
        border:3px solid ${isCurrent ? "#ff0000" : "#fff"};
        display:flex;
        align-items:center;
        justify-content:center;
        color:white;
        font-weight:700;
        font-size:14px;
        box-shadow:${
          isCurrent
            ? "0 0 10px rgba(255,0,0,0.7)"
            : "0 0 6px rgba(0,0,0,0.4)"
        };
        ${isVisited ? "opacity:0.65;" : ""}
      ">
        ${num}
      </div>`,
    iconAnchor: [15, 15],
  });

// -------------------------------
// Geometry Helpers
// -------------------------------
const toLatLng = ([a, b]) => {
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [a, b];
  return [b, a];
};

const normalizeGeometryToLatLngs = (route_data) => {
  if (!route_data?.geometry) return [];
  const geometry = route_data.geometry;

  if (Array.isArray(geometry)) {
    return geometry.map(toLatLng);
  }
  if (Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.map(toLatLng);
  }
  return [];
};

// -------------------------------
// Fit Bounds Components
// -------------------------------
function FitRouteBoundsOnData({ routeLeads }) {
  const map = useMap();
  useEffect(() => {
    if (!routeLeads || routeLeads.length === 0) return;
    const valid = routeLeads.filter(
      (l) =>
        typeof l.latitude === "number" && typeof l.longitude === "number"
    );
    if (!valid.length) return;

    const bounds = L.latLngBounds(
      valid.map((l) => [l.latitude, l.longitude])
    );
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [routeLeads, map]);

  return null;
}

function CenterOnCurrentStop({ currentStopIndex, orderedStops }) {
  const map = useMap();

  useEffect(() => {
    if (
      currentStopIndex > 0 &&
      orderedStops &&
      orderedStops.length > currentStopIndex
    ) {
      const currentStop = orderedStops[currentStopIndex];
      if (currentStop && currentStop.latitude && currentStop.longitude) {
        map.setView(
          [currentStop.latitude, currentStop.longitude],
          Math.max(map.getZoom(), 15),
          { animate: true, duration: 0.5 }
        );
      }
    }
  }, [currentStopIndex, orderedStops, map]);

  return null;
}

// -------------------------------
// Main Component
// -------------------------------
export default function SavedRoutesPage({ token }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isPhoneViewport = useMediaQuery("(max-width: 767.95px)");
  const showRouteMap = shouldShowSavedRoutesMap(isPhoneViewport);
  const isLandscape = useLandscape();
  const isLandscapeOrientation = useMediaQuery("(orientation: landscape)");
  const isShortHeight = useMediaQuery("(max-height: 700px)");
  const isMobileLandscape = isLandscapeOrientation && isShortHeight;
  const navigate = useNavigate();
  const mapRef = useRef(null);

  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeLeads, setRouteLeads] = useState([]);
  const [routeCoords, setRouteCoords] = useState([]);
  const [orderedStops, setOrderedStops] = useState([]);
  const [legsBreakdown, setLegsBreakdown] = useState([]);
  const [startAddress, setStartAddress] = useState("");
  const [startCoords, setStartCoords] = useState(null);

  const [selectedLead, setSelectedLead] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [visitedStops, setVisitedStops] = useState(new Set());

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedLeadForUpdate, setSelectedLeadForUpdate] = useState(null);

  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mobileStopsOpen, setMobileStopsOpen] = useState(false);
  const [stopsCollapsed, setStopsCollapsed] = useState(false);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [mobileRoutesCollapsed, setMobileRoutesCollapsed] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
const { role, isReadOnly } = useAuth();
const { startTour } = useTour();

  const handleBackToMap = () => navigate("/leads");

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }

    const success = async (pos) => {
      const { latitude, longitude } = pos.coords;
      const newStart = { lat: latitude, lon: longitude };
      setStartCoords(newStart);

      try {
        const address = await reverseGeocode(latitude, longitude);
        setStartAddress(address || "Current Location");
      } catch {
        setStartAddress("Current Location");
      }

      setSelectedRoute((prev) =>
        prev
          ? {
              ...prev,
              route_data: {
                ...(prev.route_data || {}),
                start_coords: newStart,
                start_address: "Current Location",
              },
            }
          : prev
      );

      toast.success("Current location set as start.");
    };

    const error = () => {
      toast.error("Unable to retrieve your location.");
    };

    navigator.geolocation.getCurrentPosition(success, error);
  }, []);

// Start tour on mount (similar to Dashboard)
useEffect(() => {
  const tourKey = "v1_5_saved_routes_tour_complete";
  if (localStorage.getItem(tourKey)) return;
  
  setTimeout(() => {
    const steps = SAVED_ROUTES_TOUR_STEPS(role, { skipMapStep: !showRouteMap });
    if (steps && steps.length > 0) {
      startTour(steps, tourKey); // Pass the key as second parameter
    }
  }, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [role, showRouteMap]);

  // Load all saved routes
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const res = await API.get("/routeplans/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoutes(res.data);
      } catch (err) {
        console.error("Error loading routes:", err);
        toast.error("Failed to load saved routes.");
      }
    };

    fetchRoutes();
  }, [token]);

  const handleViewRoute = useCallback(
    async (route) => {
      setSelectedRoute(route);
      setRouteCoords([]);
      setRouteLeads([]);
      setOrderedStops([]);
      setLegsBreakdown([]);
      setVisitedStops(new Set());
      setCurrentStopIndex(0);
      setStopsCollapsed(false);

       // 🔥 NEW — auto-collapse "Saved Routes" on mobile
    if (isSmallScreen) {
      setMobileRoutesCollapsed(true);
    }

      try {
        const res = await API.get(`/routeplans/${route.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setSelectedRoute(data);

        const latlngs = normalizeGeometryToLatLngs(data.route_data || {});
        setRouteCoords(latlngs);

        let renderStops = [];

        // Priority 1: leads array
        if (Array.isArray(data.leads) && data.leads.length > 0) {
          renderStops = data.leads.map((lead, i) => ({
            id: lead.id,
            lead_id: lead.id,
            first_name: lead.first_name,
            last_name: lead.last_name,
            name: `${lead.first_name} ${lead.last_name}`,
            address: lead.address || "—",
            city: lead.city,
            state: lead.state,
            zip_code: lead.zip_code,
            latitude: lead.latitude,
            longitude: lead.longitude,
            status: lead.status,
            phone: formatPhoneDisplay(lead.phone),
            email: lead.email,
            notes: lead.notes || "",
            __stopNumber: i + 1,
          }));
        }
        // Priority 2: route_data.stops
        else if (
          data.route_data?.stops &&
          Array.isArray(data.route_data.stops) &&
          data.route_data.stops.length > 0
        ) {
          renderStops = data.route_data.stops.map((s, i) => ({
            id: s.id ?? s.lead_id ?? `stop-${i + 1}`,
            lead_id: s.lead_id ?? null,
            first_name: s.first_name || "",
            last_name: s.last_name || "",
            name:
              s.name ||
              `${s.first_name || ""} ${s.last_name || ""}`.trim() ||
              `Stop ${i + 1}`,
            address: s.address ?? "—",
            city: s.city,
            state: s.state,
            zip_code: s.zip_code,
            latitude: s.lat ?? s.latitude,
            longitude: s.lng ?? s.longitude,
            status: s.status,
            notes: s.notes || "",
            __stopNumber: i + 1,
          }));
        }
        // Priority 3: fallback from geometry
        else if (latlngs.length > 0) {
          const enrichedStops = await Promise.all(
            latlngs.map(async ([lat, lon], i) => {
              const address = await reverseGeocode(lat, lon);
              return {
                id: `coord-${i + 1}`,
                first_name: "",
                last_name: "",
                name: `Stop ${i + 1}`,
                address,
                latitude: lat,
                longitude: lon,
                __stopNumber: i + 1,
              };
            })
          );
          renderStops = enrichedStops;
        }

        let finalStops = [];

        if (data.route_data?.start_coords) {
          finalStops.push({
            id: "start",
            lead_id: null,
            name: "Starting Point",
            address: data.route_data.start_address || "Starting Point",
            latitude: data.route_data.start_coords.lat,
            longitude: data.route_data.start_coords.lon,
            __stopNumber: 0,
          });
        }

        finalStops = [...finalStops, ...renderStops];

        setRouteLeads(finalStops);
        setOrderedStops(finalStops);

        if (data.route_data?.start_address) {
          setStartAddress(data.route_data.start_address);
        } else if (data.route_data?.start_coords) {
          reverseGeocode(
            data.route_data.start_coords.lat,
            data.route_data.start_coords.lon
          )
            .then((addr) => setStartAddress(addr || "Current Location"))
            .catch(() => setStartAddress("Current Location"));
        } else {
          setStartAddress("");
        }

        if (data.route_data?.start_coords) {
          setStartCoords(data.route_data.start_coords);
        } else {
          setStartCoords(null);
        }

        const visitedData = data.route_data?.visited || {};
        const visitedSet = new Set();
        Object.keys(visitedData).forEach((key) => {
          if (visitedData[key] === true) {
            visitedSet.add(parseInt(key, 10));
          }
        });
        setVisitedStops(visitedSet);
        // If start point exists, begin at first actual stop (index 1), otherwise start at index 0
        const initialStopIndex = data.route_data?.start_coords ? 1 : 0;
        setCurrentStopIndex(initialStopIndex);
      } catch (err) {
        console.error("Error loading route details:", err);
        toast.error("Could not load route details.");
      }
    },
    [token]
  );

  // React to external lead updates
  useEffect(() => {
    const unsubscribe = subscribeLeadUpdates(() => {
      if (selectedRoute) {
        handleViewRoute(selectedRoute);
      }
    });
    return () => unsubscribe();
  }, [selectedRoute, handleViewRoute]);

  // Trigger map resize when stops breakdown collapses in landscape mode
  useEffect(() => {
    if (!showRouteMap) return;
    if ((isLandscape || isMobileLandscape) && mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 100);
    }
  }, [stopsCollapsed, isLandscape, isMobileLandscape, showRouteMap]);

  const handleDeleteRoute = useCallback(
    (routeId) => {
      const route = routes.find(r => r.id === routeId);
      setRouteToDelete(route);
      setConfirmDelete(true);
    },
    [routes]
  );

  const handleConfirmDelete = useCallback(
    async () => {
      if (!routeToDelete) return;
      try {
        await API.delete(`/routeplans/${routeToDelete.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoutes((prev) => prev.filter((r) => r.id !== routeToDelete.id));
        toast.success("Route deleted.");

        if (selectedRoute?.id === routeToDelete.id) {
          setSelectedRoute(null);
          setRouteLeads([]);
          setRouteCoords([]);
          setOrderedStops([]);
          setLegsBreakdown([]);
          setVisitedStops(new Set());
          setCurrentStopIndex(0);
        }
      } catch (err) {
        console.error("Error deleting route:", err);
        toast.error("Failed to delete route.");
      } finally {
        setConfirmDelete(false);
        setRouteToDelete(null);
      }
    },
    [token, selectedRoute, routeToDelete]
  );

  const handleOptimizeHere = useCallback(async () => {
    if (!routeLeads || routeLeads.length < 1) {
      toast.info("Select a route with at least one stop.");
      return;
    }

    toast.info("Optimizing route...");

    try {
      const validStops = routeLeads.filter(
        (l) =>
          typeof l.latitude === "number" && 
          typeof l.longitude === "number" &&
          l.id !== "start"  // ✅ Exclude start point (added separately to OSRM request)
      );

      if (!validStops.length) {
        toast.error("No valid coordinates found for stops.");
        return;
      }

      let coordsList = [];
      const hasStart =
        selectedRoute?.route_data?.start_coords &&
        typeof selectedRoute.route_data.start_coords.lat === "number" &&
        typeof selectedRoute.route_data.start_coords.lon === "number";

      if (hasStart) {
        const { lat, lon } = selectedRoute.route_data.start_coords;
        coordsList.push(`${lon},${lat}`);
      }

      coordsList.push(
        ...validStops.map((s) => `${s.longitude},${s.latitude}`)
      );

      const coordsParam = coordsList.join(";");
      const url = `https://router.project-osrm.org/trip/v1/driving/${coordsParam}?source=${
        hasStart ? "first" : "first"
      }&roundtrip=false&overview=full&geometries=geojson`;

      const res = await axios.get(url);
      const trip = res.data?.trips?.[0];
      const waypoints = res.data?.waypoints;

      if (!trip || !trip.geometry || !trip.geometry.coordinates) {
        toast.error("No geometry returned from routing service.");
        console.error("OSRM trip response:", res.data);
        return;
      }

      const fullCoords = trip.geometry.coordinates.map(([lon, lat]) => [
        lat,
        lon,
      ]);
      if (!fullCoords.length) {
        toast.error("Empty geometry path returned.");
        return;
      }

      setRouteCoords(fullCoords);

      const stopWaypoints = hasStart ? waypoints.slice(1) : waypoints;
      // ✅ Limit waypoints to the number of stops we actually sent to prevent extra addresses
      const limitedWaypoints = stopWaypoints.slice(0, validStops.length);

      // Preserve input index before sorting: waypoint_index = trip order, inputIndex = which validStop
      const withInputIndex = limitedWaypoints.map((wp, i) => ({ wp, inputIndex: i }));
      const sortedByTrip = withInputIndex.sort(
        (a, b) => a.wp.waypoint_index - b.wp.waypoint_index
      );

      // ✅ Filter out nulls to maintain stop count invariant
      const orderedByTrip = (await Promise.all(
        sortedByTrip.map(async ({ wp, inputIndex }, idx) => {
          // Use inputIndex (position in input array) to look up stop data, NOT waypoint_index
          let stop = validStops[inputIndex];

          if (!stop || stop.id === "start") {
            stop = validStops.find((s) => {
              if (!s.latitude || !s.longitude || s.id === "start") return false;
              const latDiff = Math.abs(s.latitude - wp.location[1]);
              const lonDiff = Math.abs(s.longitude - wp.location[0]);
              return latDiff < 0.0001 && lonDiff < 0.0001;
            });
          }

          if (stop && stop.id && stop.id !== "start") {
            return {
              ...stop,
              __stopNumber: idx + 1,
              address:
                stop.address &&
                !stop.address.startsWith("Lat:") &&
                stop.address !== "—" &&
                stop.address.trim() !== ""
                  ? stop.address
                  : (await reverseGeocode(
                      wp.location[1],
                      wp.location[0]
                    )) || stop.address || "—",
              latitude: wp.location[1],
              longitude: wp.location[0],
            };
          }

          // ✅ Guard: Skip unmatched waypoints instead of creating synthetic stops
          if (!stop) {
            console.warn("Skipping unmatched OSRM waypoint", wp);
            return null;
          }

          // Fallback should never be reached, but keep for safety
          return null;
        })
      )).filter(Boolean);

      let finalStops = [...orderedByTrip];

      if (
        selectedRoute?.route_data?.start_address &&
        selectedRoute?.route_data?.start_coords
      ) {
        finalStops.unshift({
          id: "start",
          address: selectedRoute.route_data.start_address,
          latitude: selectedRoute.route_data.start_coords.lat,
          longitude: selectedRoute.route_data.start_coords.lon,
          __stopNumber: 0,
        });
      }

      setOrderedStops(finalStops);
      setRouteLeads(finalStops);

      setVisitedStops(new Set());

      const firstStopIndex = finalStops[0]?.id === "start" ? 1 : 0;
      setCurrentStopIndex(firstStopIndex);

      const legs = trip.legs || [];
      // ✅ Ensure legs count matches stops count (legs = stops - 1)
      // Slice to prevent index out of bounds if OSRM returns unexpected leg count
      const safeLegsCount = Math.min(legs.length, finalStops.length - 1);
      const segments = legs
        .slice(0, safeLegsCount)
        .map((leg, idx) => {
          // ✅ Additional safety check to prevent undefined access
          if (idx + 1 >= finalStops.length) {
            console.warn(`Skipping leg ${idx}: no corresponding stop at index ${idx + 1}`);
            return null;
          }
          return {
            from: finalStops[idx],
            to: finalStops[idx + 1],
            distance_km: (leg.distance || 0) / 1000,
            duration_min: (leg.duration || 0) / 60,
          };
        })
        .filter(Boolean); // Remove any null entries from safety checks
      setLegsBreakdown(segments);

      setSelectedRoute((prev) =>
        prev
          ? {
              ...prev,
              route_data: {
                ...(prev.route_data || {}),
                geometry: trip.geometry,
                distance_km: (trip.distance || 0) / 1000,
                duration_min: (trip.duration || 0) / 60,
              },
            }
          : prev
      );

      toast.success("✅ Route optimized and redrawn!");

      if (isSmallScreen || isLandscape || isMobileLandscape) {
        setStopsCollapsed(true);
      }
    } catch (err) {
      console.error("Optimization failed:", err);
      toast.error("Failed to compute optimized route.");
    }
  }, [routeLeads, selectedRoute, isSmallScreen, isLandscape, isMobileLandscape]);

  const handleClearPolyline = useCallback(() => {
    if (!selectedRoute) {
      toast.info("No active route to clear.");
      return;
    }

    setRouteCoords([]);
    setRouteLeads([]);
    setLegsBreakdown([]);
    setVisitedStops(new Set());
    setCurrentStopIndex(0);
    setOrderedStops([]);
    setSelectedRoute(null);
    setStartCoords(null);
    setStartAddress("");
    setStopsCollapsed(false);

    toast.success("Route cleared.");
  }, [selectedRoute]);

  const handleUpdateStatus = useCallback(
    (lead, idx) => {
      const wasVisited = visitedStops.has(idx);
      setSelectedLeadForUpdate({ lead, idx, wasVisited });
      setStatusDialogOpen(true);
    },
    [visitedStops]
  );

  const handleSaveStatus = useCallback(
    async ({ status, notes, markVisited, autoAdvance, navigateToNext, follow_up_date }) => {
      const { lead, idx } = selectedLeadForUpdate || {};
      const rawLeadId = lead?.lead_id ?? lead?.id;

      if (!lead || rawLeadId === undefined || rawLeadId === null) {
        toast.error("Invalid lead.");
        return;
      }

      const leadIdString = String(rawLeadId);
      const isSyntheticId =
        leadIdString.startsWith("coord-") ||
        leadIdString.startsWith("optimized-") ||
        leadIdString.startsWith("stop-");
      const numericLeadId = /^\d+$/.test(leadIdString)
        ? Number(leadIdString)
        : null;
      const isValidLeadId = !isSyntheticId && numericLeadId !== null;

      try {
        if (isValidLeadId) {
          try {
            await API.patch(`/leads/${numericLeadId}/`, {
              status,
              notes: notes || lead.notes || "",
              assigned_rep: lead.assigned_rep,
              ...(follow_up_date && { follow_up_date }), // Add follow-up date if provided
            });
            emitLeadUpdate({ id: numericLeadId, status, notes });
          } catch (err) {
            console.error("Error updating lead:", err);
            if (err.response?.status === 404) {
              toast.warn(
                "Lead not found in database. Status updated locally only."
              );
            } else {
              throw err;
            }
          }
        }

        const currentVisited = selectedRoute?.route_data?.visited || {};
        const updatedVisited = { ...currentVisited };
        if (markVisited) {
          updatedVisited[idx] = true;
        } else {
          delete updatedVisited[idx];
        }

        if (selectedRoute) {
          await API.patch(`/routeplans/${selectedRoute.id}/`, {
            route_data: {
              ...(selectedRoute.route_data || {}),
              visited: updatedVisited,
            },
          });
        }

        setVisitedStops((prev) => {
          const next = new Set(prev);
          if (markVisited) next.add(idx);
          else next.delete(idx);
          return next;
        });

        setOrderedStops((prev) =>
          prev.map((s, i) =>
            i === idx ? { ...s, status, notes: notes || s.notes } : s
          )
        );
        setRouteLeads((prev) =>
          prev.map((s, i) =>
            i === idx ? { ...s, status, notes: notes || s.notes } : s
          )
        );

        toast.success("Status updated.");

        if (autoAdvance && idx < orderedStops.length - 1) {
          const nextIdx = idx + 1;
          setCurrentStopIndex(nextIdx);

          if (navigateToNext && isDeviceMobile()) {
            const nextStop = orderedStops[nextIdx];
            if (nextStop && nextStop.latitude && nextStop.longitude) {
              setTimeout(() => {
                const address =
                  nextStop.address ||
                  `${nextStop.first_name || ""} ${
                    nextStop.last_name || ""
                  }, ${nextStop.city || ""} ${formatStateAbbr(nextStop.state)}`;
                openNavigation(
                  address,
                  nextStop.latitude,
                  nextStop.longitude
                );
              }, 500);
            }
          }
        }

        setStatusDialogOpen(false);
        setSelectedLeadForUpdate(null);
      } catch (err) {
        console.error("Error saving status:", err);
        toast.error("Failed to update status.");
      }
    },
    [selectedLeadForUpdate, selectedRoute, orderedStops]
  );

  const handleNavigateToCurrent = useCallback(() => {
    if (currentStopIndex === 0) {
      toast.info("Start point cannot be navigated to.");
      return;
    }
    const currentStop = orderedStops[currentStopIndex];
    if (!currentStop) {
      toast.info("Select a stop to navigate to.");
      return;
    }

    const address =
      currentStop.address ||
      `${currentStop.first_name} ${currentStop.last_name}, ${currentStop.city}, ${formatStateAbbr(currentStop.state)}`;

    if (currentStop.latitude && currentStop.longitude) {
      openNavigation(address, currentStop.latitude, currentStop.longitude);
    } else {
      toast.error("No coordinates available for this stop.");
    }
  }, [currentStopIndex, orderedStops]);

  const handleNavigateToNext = useCallback(() => {
    const nextIndex = currentStopIndex + 1;
    if (nextIndex >= orderedStops.length) {
      toast.info("You've reached the end of the route!");
      return;
    }

    const nextStop = orderedStops[nextIndex];
    const address =
      nextStop.address ||
      `${nextStop.first_name} ${nextStop.last_name}, ${nextStop.city}, ${formatStateAbbr(nextStop.state)}`;

    if (nextStop.latitude && nextStop.longitude) {
      openNavigation(address, nextStop.latitude, nextStop.longitude);
      setCurrentStopIndex(nextIndex);
    } else {
      toast.error("No coordinates available for next stop.");
    }
  }, [currentStopIndex, orderedStops]);

  const stopCount = useMemo(() => {
    if (
      selectedRoute?.route_data?.stops &&
      Array.isArray(selectedRoute.route_data.stops)
    ) {
      return selectedRoute.route_data.stops.length;
    }
    if (Array.isArray(selectedRoute?.leads)) {
      return selectedRoute.leads.length;
    }
    if (
      selectedRoute?.route_data?.geometry?.coordinates &&
      Array.isArray(selectedRoute.route_data.geometry.coordinates)
    ) {
      return selectedRoute.route_data.geometry.coordinates.length;
    }
    if (
      selectedRoute?.route_data?.geometry &&
      Array.isArray(selectedRoute.route_data.geometry)
    ) {
      return selectedRoute.route_data.geometry.length;
    }
    return "—";
  }, [selectedRoute]);

  const toggleMobileStops = () => {
    setMobileStopsOpen((prev) => !prev);
  };

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <DirectionsIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Saved Routes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {showRouteMap
                    ? "View and manage your saved routes. Click on any route to view it on the map."
                    : "View and manage your saved routes. Optimize travel distance and minimize wasted time."}
                </Typography>
              </Box>
            </Stack>

            <Box
              data-tour="back-to-map"
              sx={{ display: "inline-block" }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleBackToMap}
                sx={{ whiteSpace: "nowrap" }}
              >
                ← Back to Lead Map
              </Button>
            </Box>
          </Stack>
        </Card>

        {/* --------------------------------------------------------------------------------------------Saved Routes List */}
      <Paper

	data-tour="routes-list"
        elevation={1}
        sx={{
          mb: 2,
          p: { xs: 1, md: 1.5 },
        }}
      >
        {!isSmallScreen ? (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Route Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Stops</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routes.map((route, i) => {
                  const stopsCount =
                    route.route_data?.stops?.length ||
                    route.leads?.length ||
                    route.route_data?.geometry?.coordinates?.length ||
                    route.route_data?.geometry?.length ||
                    "—";
                  return (
                    <TableRow
                      key={route.id}
                      hover
                      selected={selectedRoute?.id === route.id}
                    >
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{route.name || `Route ${route.id}`}</TableCell>
                      <TableCell>
                        {route.date || route.created_at?.split("T")[0] || "—"}
                      </TableCell>
                      <TableCell>{stopsCount}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ mr: 1, textTransform: "none" }}
                          onClick={() => handleViewRoute(route)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{ textTransform: "none" }}
                          onClick={() => handleDeleteRoute(route.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {routes.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", py: 1.5, color: "text.secondary" }}
              >
                No saved routes yet. Create and save a route from the Lead Map.
              </Typography>
            ) : (
              <>
                {/* -------------------------------------------------Mobile header with collapse toggle */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setMobileRoutesCollapsed((prev) => !prev)
                  }
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, fontSize: 15 }}
                  >
                    Saved Routes ({routes.length})
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary.main"
                    sx={{ fontWeight: 500 }}
                  >
                    {mobileRoutesCollapsed ? "Show ▼" : "Hide ▲"}
                  </Typography>
                </Box>

                {/* -------------------------------------------------Collapsible mobile cards */}
                {!mobileRoutesCollapsed && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                    }}
                  >
                    {routes.map((route, i) => {
                      const stopsCount =
                        route.route_data?.stops?.length ||
                        route.leads?.length ||
                        route.route_data?.geometry?.coordinates?.length ||
                        route.route_data?.geometry?.length ||
                        "—";
                      return (
                        <Card
                          key={route.id}
                          elevation={selectedRoute?.id === route.id ? 4 : 1}
                          sx={{
                            borderRadius: 2,
                            border:
                              selectedRoute?.id === route.id
                                ? `1px solid ${theme.palette.primary.main}`
                                : `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <CardContent sx={{ pb: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                mb: 0.5,
                              }}
                            >
                              <Box sx={{ flex: 1, pr: 1 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600, fontSize: 15 }}
                                >
                                  #{i + 1}{" "}
                                  {route.name || `Route ${route.id}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: 12,
                                    color: "text.secondary",
                                  }}
                                >
                                  Date:{" "}
                                  {route.date ||
                                    route.created_at?.split("T")[0] ||
                                    "—"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: 12,
                                    color: "text.secondary",
                                  }}
                                >
                                  Stops: {stopsCount}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                fullWidth
                                sx={{ textTransform: "none" }}
                                onClick={() => handleViewRoute(route)}
                              >
                                View Route
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                sx={{ textTransform: "none" }}
                                onClick={() => handleDeleteRoute(route.id)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* ----------------------------------------------------------------------------------------Stops Breakdown */}
      {orderedStops.length > 0 && (
        <Paper
          data-tour="stops-breakdown"
          elevation={3}
          sx={{
            mb: 2,
            p: { xs: 1.25, md: 2 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            maxHeight: { xs: "65vh", md: "60vh" },
            position: (isSmallScreen || isLandscape || isMobileLandscape) ? "relative" : undefined,
            overflow: (isSmallScreen || isLandscape || isMobileLandscape) ? "hidden" : undefined,
            overflowY: (!isSmallScreen && !isLandscape && !isMobileLandscape) ? "auto" : undefined,
            display: (isSmallScreen || isLandscape || isMobileLandscape) ? "flex" : undefined,
            flexDirection: (isSmallScreen || isLandscape || isMobileLandscape) ? "column" : undefined,
            position: "relative",
          }}
        >
          {/* -----------------------------------------------------------------------------------Header + collapse toggle (mobile only) */}
          <Box
            sx={{
              flex: (isSmallScreen || isLandscape || isMobileLandscape) ? 1 : undefined,
              minHeight: (isSmallScreen || isLandscape || isMobileLandscape) ? 0 : undefined,
              overflowY: (isSmallScreen || isLandscape || isMobileLandscape) ? "auto" : undefined,
              pb: (isSmallScreen || isLandscape || isMobileLandscape) ? 10 : undefined,
            }}
          >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Stops Breakdown (Quick Edit)
            </Typography>
            {(isSmallScreen || isLandscape || isMobileLandscape) && (
              <Button
                size="small"
                onClick={() => setStopsCollapsed((prev) => !prev)}
                sx={{ textTransform: "none" }}
              >
                {stopsCollapsed ? "Expand" : "Collapse"}
              </Button>
            )}
          </Box>

          {/* ----------------------------------------------------------------------Collapsible section – desktop always expanded, mobile collapsible */}
          {((!isSmallScreen && !isLandscape && !isMobileLandscape) || !stopsCollapsed) && (
            <>
              {/* -----------------Controls: Optimize + Clear----------------- */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 1,
                  mb: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    if ((isSmallScreen || isLandscape || isMobileLandscape) && (!startCoords || !startAddress)) {
                      setShowStartWarning(true);
                      return;
                    }
                    handleOptimizeHere();
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Optimize Route
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowClearConfirm(true)}
                  sx={{ textTransform: "none" }}
                >
                  🗑️ Clear Route
                </Button>
              </Box>

              {/* ------------------------------------------------------------------------------DESKTOP TABLE */}
              {!isSmallScreen && !isLandscape && !isMobileLandscape && (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Address
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Notes
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Distance / Duration
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* ----------------------------------------------------------------Start Row */}
                      <TableRow>
                        <TableCell>Start</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Click 'Use Current' to set start location"
                              value={startAddress}
                              readOnly
                              disabled={!startAddress}
                              style={{
                                flex: 1,
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                                fontSize: "13px",
                                backgroundColor: startAddress
                                  ? "#f5f5f5"
                                  : "#fafafa",
                                cursor: "not-allowed",
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleUseCurrentLocation}
                              sx={{
                                minWidth: "auto",
                                px: 1,
                                fontSize: "11px",
                                textTransform: "none",
                              }}
                            >
                              📍 Use Current
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                        <TableCell>—</TableCell>
                      </TableRow>

                      {/* -------------------------------------------------------------------------------------Regular Stops- */}
                      {orderedStops
                        .filter((stop) => stop.id !== "start")
                        .map((lead, displayIdx) => {
                          const actualIdx = orderedStops.findIndex(
                            (s) => s.id === lead.id
                          );
                          const isVisited = visitedStops.has(actualIdx);
                          const seg = legsBreakdown[actualIdx - 1];
                          const addr =
                            lead.address ||
                            lead.name ||
                            [
                              lead.address,
                              lead.city,
                              formatStateAbbr(lead.state),
                              lead.zip_code,
                            ]
                              .filter(Boolean)
                              .join(", ") ||
                            "-";
                          const miles = seg
                            ? (seg.distance_km * 0.621371).toFixed(1)
                            : null;

                          return (
                            <TableRow
                              key={lead.id || actualIdx}
                              sx={{
                                opacity: isVisited ? 0.55 : 1,
                                backgroundColor: (theme) =>
                                  isVisited
                                    ? theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.03)"
                                      : "rgba(0,0,0,0.02)"
                                    : "transparent",
                                transition: "opacity 0.3s ease",
                              }}
                            >
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {isVisited && (
                                    <CheckCircle
                                      fontSize="small"
                                      sx={{
                                        color: "success.main",
                                        opacity: 0.6,
                                        fontSize: "16px",
                                      }}
                                    />
                                  )}
                                  <Typography
                                    sx={{
                                      color: isVisited
                                        ? "text.secondary"
                                        : "text.primary",
                                      fontWeight: isVisited ? 400 : 500,
                                    }}
                                  >
                                    {displayIdx + 1}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isVisited
                                      ? "text.secondary"
                                      : "text.primary",
                                  }}
                                >
                                  {lead.first_name && lead.last_name
                                    ? `${lead.first_name} ${lead.last_name}`
                                    : lead.name || ""}
                                  {((lead.first_name && lead.last_name) ||
                                    lead.name) &&
                                  addr &&
                                  addr !== "—" ? (
                                    <br />
                                  ) : null}
                                  {addr && addr !== "—"
                                    ? addr
                                    : lead.city && lead.state
                                    ? `${lead.city}, ${formatStateAbbr(lead.state)}${
                                        lead.zip_code
                                          ? ` ${lead.zip_code}`
                                          : ""
                                      }`
                                    : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isVisited
                                      ? "text.secondary"
                                      : "text.primary",
                                  }}
                                >
                                  {lead.status && lead.status !== "—"
                                    ? lead.status
                                    : "New"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isVisited
                                      ? "text.secondary"
                                      : "text.primary",
                                    maxWidth: 200,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={lead.notes || ""}
                                >
                                  {lead.notes ? lead.notes : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {seg ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {miles} mi /{" "}
                                    {seg.duration_min.toFixed(0)} min
                                  </Typography>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ textTransform: "none" }}
                                  onClick={() =>
                                    handleUpdateStatus(lead, actualIdx)
                                  }
                                  disabled={actualIdx === 0}
                                >
                                  Update Status
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* ---------------------------------------------------------------------------------------MOBILE CARDS (inside collapse)-- */}
              {(isSmallScreen || isLandscape || isMobileLandscape) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {/* Start "row" as card */}
                  <Card
                    variant="outlined"
                    onClick={() => setCurrentStopIndex(0)}
                    sx={{
                      mb: 0.5,
                      ...mobileRouteStopCardSx(currentStopIndex === 0, false),
                    }}
                  >
                    <CardContent sx={{ pb: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Start • Starting Point
                        </Typography>
                        {currentStopIndex === 0 && (
                          <Chip label="CURRENT" size="small" sx={mobileCurrentStopPillSx} />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Click 'Use Current' to set start location"
                          value={startAddress}
                          readOnly
                          disabled={!startAddress}
                          style={{
                            width: "100%",
                            padding: "6px 10px",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            fontSize: "13px",
                            backgroundColor: startAddress
                              ? "#f5f5f5"
                              : "#fafafa",
                            cursor: "not-allowed",
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleUseCurrentLocation}
                          sx={{ textTransform: "none" }}
                        >
                          📍 Use Current Location
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>

                  {orderedStops
                    .filter((stop) => stop.id !== "start")
                    .map((lead, displayIdx) => {
                      const actualIdx = orderedStops.findIndex(
                        (s) => s.id === lead.id
                      );
                      const isVisited = visitedStops.has(actualIdx);
                      const seg = legsBreakdown[actualIdx - 1];
                      const addr =
                        [
                          lead.address,
                          lead.city,
                          formatStateAbbr(lead.state),
                          lead.zip_code,
                        ]
                          .filter(Boolean)
                          .join(", ") ||
                        lead.name ||
                        "—";
                      const miles = seg
                        ? (seg.distance_km * 0.621371).toFixed(1)
                        : null;

                      const isCurrent = currentStopIndex === actualIdx;

                      return (
                        <Card
                          key={lead.id || actualIdx}
                          variant="outlined"
                          sx={mobileRouteStopCardSx(isCurrent, isVisited)}
                          onClick={() => setCurrentStopIndex(actualIdx)}
                        >
                          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 14,
                                  flex: 1,
                                  minWidth: 0,
                                  color: isVisited && !isCurrent
                                    ? "text.secondary"
                                    : "text.primary",
                                }}
                              >
                                #{displayIdx + 1}{" "}
                                {lead.first_name && lead.last_name
                                  ? `${lead.first_name} ${lead.last_name}`
                                  : lead.name || "Stop"}
                              </Typography>
                              {isCurrent && (
                                <Chip label="CURRENT" size="small" sx={mobileCurrentStopPillSx} />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: 14,
                                lineHeight: 1.4,
                                color: "text.secondary",
                                mt: 0.25,
                              }}
                            >
                              {addr && addr !== "—"
                                ? addr
                                : lead.city && lead.state
                                ? `${lead.city}, ${formatStateAbbr(lead.state)}${
                                    lead.zip_code ? ` ${lead.zip_code}` : ""
                                  }`
                                : "Address not available"}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: isCurrent ? "success.main" : "text.secondary",
                                  fontSize: 12,
                                  fontWeight: isCurrent ? 600 : 400,
                                }}
                              >
                                {lead.status && lead.status !== "—" ? lead.status : "New"}
                                {seg && <> • {miles} mi / {seg.duration_min.toFixed(0)} min</>}
                              </Typography>
                              {isVisited && (
                                <CheckCircle
                                  fontSize="small"
                                  sx={{ color: "success.main", fontSize: 16 }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1,
                                fontSize: 12,
                                color: "text.secondary",
                              }}
                            >
                              Notes: {lead.notes?.trim() ? lead.notes : "None"}
                            </Typography>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(lead, actualIdx);
                              }}
                              sx={{
                                mt: 1.25,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 12,
                              }}
                            >
                              Update
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                </Box>
              )}
            </>
          )}

          {/* ---------------------------------------------------------------------------Navigation: Desktop = Next Stop; Mobile = Floating pill */}
          {!(isSmallScreen || isLandscape || isMobileLandscape) && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 2,
                flexDirection: "row",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  if (currentStopIndex < orderedStops.length - 1) {
                    setCurrentStopIndex((prev) => prev + 1);
                  }
                }}
                disabled={currentStopIndex >= orderedStops.length - 1}
                sx={{ textTransform: "none" }}
              >
                ⏭️ Next Stop
              </Button>
            </Box>
          )}

          {(isSmallScreen || isLandscape || isMobileLandscape) && (
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: 12,
                right: 12,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  maxWidth: "100%",
                  borderRadius: "9999px",
                  bgcolor: "background.paper",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: 36 }}>
                {currentStopIndex + 1}/{orderedStops.length}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<NavigationIcon sx={{ fontSize: 18 }} />}
                onClick={handleNavigateToCurrent}
                disabled={currentStopIndex === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 1.5,
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  flexShrink: 1,
                  overflow: "hidden",
                  "& .MuiButton-startIcon": { flexShrink: 0 },
                }}
              >
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  Navigate to Stop {currentStopIndex === 0 ? 1 : currentStopIndex}
                </Box>
              </Button>
              <IconButton
                size="small"
                onClick={() => setCurrentStopIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentStopIndex === 0}
                sx={{ bgcolor: "action.hover" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setCurrentStopIndex((prev) => Math.min(orderedStops.length - 1, prev + 1))}
                disabled={currentStopIndex >= orderedStops.length - 1}
                sx={{ bgcolor: "action.hover" }}
              >
                <ChevronRightIcon />
              </IconButton>
              </Box>
            </Box>
          )}
          </Box>
        </Paper>
      )}

      {/*---------------------------------------------------------------------------------------------- Map- */}
      {showRouteMap && (
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          mb: 1,
          ...((isLandscape || isMobileLandscape) && stopsCollapsed && {
            height: "90vh",
            minHeight: "90vh",
          }),
        }}
      >
        <MapContainer
data-tour="saved-routes-map" 
          id="saved-routes-map"
          center={[28.5, -81.3]}
          zoom={10}
          style={{
            height: (isLandscape || isMobileLandscape)
              ? stopsCollapsed
                ? "90vh" // Double/taller height when collapsed in landscape
                : "65vh"
              : isSmallScreen
              ? stopsCollapsed
                ? "65vh"
                : "50vh"
              : "40vh",
            width: "100%",
            minHeight: (isLandscape || isMobileLandscape) && stopsCollapsed
              ? "90vh"
              : undefined,
          }}
          whenCreated={(mapInstance) => {
            mapRef.current = mapInstance;
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitRouteBoundsOnData routeLeads={routeLeads} />
          <CenterOnCurrentStop
            currentStopIndex={currentStopIndex}
            orderedStops={orderedStops}
          />

          {/* ----------------------------------------------------------------------------------Start marker */}
          {selectedRoute?.route_data?.start_coords && (
            <Marker
              position={[
                selectedRoute.route_data.start_coords.lat,
                selectedRoute.route_data.start_coords.lon,
              ]}
              icon={L.divIcon({
                className: "start-flag",
                html: `<div style="font-size:20px; color:red;">🏁</div>`,
                iconAnchor: [10, 20],
              })}
            >
              <Popup>
                <strong>Starting Point</strong>
                <br />
                {selectedRoute.route_data.start_address}
              </Popup>
            </Marker>
          )}

          {/* -------------------------------------------------------------------------------------Lead pins */}
          {routeLeads
            .filter(
              (l) =>
                typeof l.latitude === "number" &&
                typeof l.longitude === "number" &&
                l.id !== "start" &&
                l.__stopNumber !== 0
            )
            .map((lead, idx) => {
              const actualIdx = orderedStops.findIndex(
                (s) => s.id === lead.id
              );
              const isCurrent = currentStopIndex === actualIdx;
              const isVisited = visitedStops.has(actualIdx);

              let markerColor = "#F39C12"; // default orange
              if (isVisited) markerColor = "#6C757D";
              else if (isCurrent) markerColor = "#2ECC71";
              else markerColor = getPinColor(lead.status);

              const pinNumber = lead.__stopNumber || actualIdx;

              return (
                <Marker
                  key={lead.id || idx}
                  position={[lead.latitude, lead.longitude]}
                  icon={createNumberedPin(
                    pinNumber,
                    markerColor,
                    isCurrent,
                    isVisited
                  )}
                  eventHandlers={{
                    click: () => {
                      if (isSmallScreen) {
                        const leadIdx = orderedStops.findIndex((s) => s.id === lead.id);
                        setSelectedLeadForUpdate({
                          lead: lead,
                          idx: leadIdx,
                          wasVisited: visitedStops.has(leadIdx),
                        });
                        setStatusDialogOpen(true);
                      }
                    },
                  }}
                />
              );
            })}

          {routeCoords.length > 0 && (
            <Polyline positions={routeCoords} color="#007bff" weight={4} />
          )}
        </MapContainer>

      </Box>
      )}

      {/* --------------------------------------------------------------------------------------------Mobile Bottom Sheet for Stops */}
      {isSmallScreen && (
        <Box
          sx={{
            position: "fixed",
            bottom: mobileStopsOpen ? 0 : "-70vh",
            left: 0,
            width: "100%",
            height: "70vh",
            bgcolor: "background.paper",
            zIndex: 2000,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: "0px -4px 16px rgba(0,0,0,0.25)",
            transition: "bottom 0.35s ease",
            overflowY: "auto",
            p: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: "text.disabled",
              borderRadius: 2,
              mx: "auto",
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Route Stops
          </Typography>

          {orderedStops
            .filter((s) => s.id !== "start")
            .map((stop, i) => (
              <Card
                key={stop.id || i}
                sx={{
                  mb: 1.5,
                  borderRadius: 2,
                  p: 1,
                  cursor: "pointer",
                  boxShadow: 2,
                }}
                onClick={() => {
                  const stopIdx = orderedStops.findIndex((s) => s.id === stop.id);
                  setSelectedLeadForUpdate({
                    lead: stop,
                    idx: stopIdx,
                    wasVisited: visitedStops.has(stopIdx),
                  });
                  setStatusDialogOpen(true);
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  #{i + 1} {stop.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {stop.address}
                </Typography>
              </Card>
            ))}
        </Box>
      )}


      {/* ---------------------------------------------------------------------------------------------------Clear Route Confirm Dialog */}
      <Dialog
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            bgcolor: "background.paper",
            boxShadow: 8,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Clear Route?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove all pins and the route path from the map. The route
            will remain saved, but all stops will be cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowClearConfirm(false)}
            color="inherit"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowClearConfirm(false);
              handleClearPolyline();
            }}
            color="error"
            variant="contained"
            size="small"
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------------------------------------------------------Mobile Start Warning Dialog */}
      <Dialog
        open={showStartWarning}
        onClose={() => setShowStartWarning(false)}
        fullWidth
        maxWidth="xs"
        sx={{
          display: (isSmallScreen || isLandscape || isMobileLandscape) ? "flex" : "none",
        }}
        PaperProps={{
          sx: {
            ...((isLandscape || isMobileLandscape) && {
              maxWidth: "90vw",
              width: "90vw",
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "1.1rem",
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          Current Address Required
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: "center", mb: 2 }}>
            Please select your starting location before optimizing your route.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => setShowStartWarning(false)}
            sx={{ textTransform: "none" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------------------------------------------------------------------------------------------Lead action panel (if you still use it) */}
      <LeadActionPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        lead={selectedLead}
        onNext={() => {
          const idx = routeLeads.findIndex((l) => l.id === selectedLead?.id);
          const next = routeLeads[idx + 1];
          if (next) setSelectedLead(next);
          else setPanelOpen(false);
        }}
      />

      <StatusUpdateDialog
        open={statusDialogOpen}
        onClose={() => {
          setStatusDialogOpen(false);
          setSelectedLeadForUpdate(null);
        }}
        lead={selectedLeadForUpdate?.lead}
        currentStatus={selectedLeadForUpdate?.lead?.status}
        initiallyVisited={selectedLeadForUpdate?.wasVisited}
        onSave={handleSaveStatus}
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setRouteToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Route"
        message="Are you sure you want to delete this route permanently? This action cannot be undone."
        itemName={routeToDelete?.name ? `Route: ${routeToDelete.name}` : null}
      />
      </Box>
    </Box>
  );
}
